import { createPopper } from '@popperjs/core';
import { Controller } from 'stimulus';
import { PopperModifiers } from '@/js/util/popper';

// Distance between the popup and the item it describes, and the smallest gap it will leave
// against the edge of the viewport before it flips or slides to stay clear of it.
const TARGET_GAP_PX = 12;
const VIEWPORT_EDGE_PX = 8;

export default class extends Controller {
  static targets = ['duration', 'modifier', 'countdown', 'header', 'action'];

  connect() {
    // The markup holds the default wording, so the template stays the one place to edit copy.
    this.defaultAction = this.actionTarget.textContent;
  }

  disconnect() {
    this.#destroyInstance();
  }

  // `durationMs` is what makes this a countdown: a dwell-to-replace flow passes it and gets the
  // draining ring plus the header counting it down. A flow with nothing to count down leaves it
  // out and gets just the modifier line — the popup is then a plain hint, not a timer.
  show({ detail }) {
    const { durationMs, modifierLabel, action, targetCorners } = detail;
    const counting = Boolean(durationMs);

    this.countdownTarget.classList.toggle('hidden', !counting);
    this.headerTarget.classList.toggle('hidden', !counting);
    if (counting) {
      this.element.style.setProperty('--replace-wait', `${durationMs}ms`);
      this.durationTarget.textContent = `${Number((durationMs / 1000).toFixed(2))}s`;
    }
    if (modifierLabel) {
      this.modifierTarget.textContent = modifierLabel;
    }
    // Always reassigned: the element outlives a single drag, so wording left by one flow would
    // otherwise show up in the other.
    this.actionTarget.textContent = action || this.defaultAction;

    // Unhide before positioning: a display:none element has no box for Popper to measure.
    // Popper positions in a microtask, which still lands before the first paint.
    this.element.classList.remove('hidden');
    this.#position(targetCorners);
  }

  hide() {
    this.element.classList.add('hidden');
    this.#destroyInstance();
  }

  #position(corners) {
    this.#destroyInstance();
    if (!corners) {
      return;
    }

    this.instance = createPopper(this.#referenceFor(corners), this.element, {
      placement: 'top',
      // The popup is position:fixed and the corners are viewport coordinates, so Popper has to
      // measure in the same frame of reference.
      strategy: 'fixed',
      modifiers: new PopperModifiers()
        .offset([0, TARGET_GAP_PX])
        .flip(VIEWPORT_EDGE_PX)
        .preventOverflow(VIEWPORT_EDGE_PX)
        .toArray(),
    });
  }

  // Corners arrive as viewport coordinates that already account for the item's scale and rotation
  // and for the board's zoom, so min/max over the four gives the box the user actually sees.
  // Anchoring to it through a virtual element keeps the one measurement shared with the outline.
  #referenceFor(corners) {
    const xs = corners.map(([x]) => x);
    const ys = corners.map(([, y]) => y);
    const left = Math.min(...xs);
    const top = Math.min(...ys);
    const rect = new DOMRect(left, top, Math.max(...xs) - left, Math.max(...ys) - top);

    return { getBoundingClientRect: () => rect };
  }

  #destroyInstance() {
    this.instance?.destroy();
    this.instance = null;
  }
}
